import React, { useRef, useEffect, useContext } from 'react';
import { makeStyles, useTheme } from '@mui/styles';
import PropTypes from 'prop-types';
import {
    select as D3select,
    scaleTime as D3scaleTime,
    scaleOrdinal as D3scaleOrdinal,
} from 'd3';
import D3Sparkline from '../../d3/D3Sparkline';

import { GroupsContext } from '../../data/groups-service';

const useStyles = makeStyles(theme => ({
    root: {
        fill: theme.palette.info.light,

        '& .linechart': {
            strokeWidth: 1.5,
            fill: 'none',
            pointerEvents: 'none',
            // this is intentionally not an svg path attribute
            // it will be picked up programmatically and applied if no override was provided.
            // some svg attributes (fill, stroke) cannot be overridden in code
            color: theme.palette.info.light,
        },

        '& .linechartarea': {
            fillOpacity: 0.2,
            pointerEvents: 'none',
            // see note on .linechart
            color: theme.palette.info.light,
        },
    },
}));

const Sparkline = ({
    width = 200,
    height = 30,
    type = 'bar',
    ...props
}) => {
    const d3Container = useRef(null);
    const classes = useStyles();
    const theme = useTheme();

    const { groupKeys } = useContext(GroupsContext);

    useEffect(() => {
        const colors = D3scaleOrdinal()
            .range(theme.SeverityColors.concat(theme.ChartColors))
            .domain(groupKeys);

        const data = props.data;
        if (d3Container.current && data) {
            const node = d3Container.current;
            const start = new Date(data.start);
            let end = new Date(data.end);
            if (isNaN(end.getTime())) end = new Date();
            const g = D3select(node)
                .data([{
                    metric: { key: props.uuid, type, value: props.dataservice.count, },
                    timeline: data.objects,
                }])
                .join('g')
                .attr('class', 'root')
                .call(D3Sparkline()
                    .theme(theme)
                    .settings({
                        margin: 0,
                        labelWidth: 0,
                        chartHeight: height,
                        barWidth: Math.max(width / (data.objects.length || 1) - 5, 2),
                        scale: 'linear',
                    })
                    .color(() => props.color
                        ? props.color
                        : props.colorKey
                            ? colors(props.colorKey)
                            : theme.palette.info.light)
                    .x(D3scaleTime()
                        .range([0, width])
                        .domain([start, end]))
                    .xval(props.dataservice.timestamp));
            g.exit().remove();
        }
    }, [
        groupKeys,
        theme,
        height,
        props.uuid,
        type,
        width,
        props.colorKey,
        props.color,
        props.data,
        props.dataservice.count,
        props.dataservice.timestamp,
    ]);

    return (
        <span className={classes.root}>
            <svg height={height} width={width} ref={d3Container}>
            </svg>
        </span>
    );
};

Sparkline.propTypes = {
    dataservice: PropTypes.object.isRequired,
    uuid: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    // colors: PropTypes.array,
    // colorKeys: PropTypes.array,
    colorKey: PropTypes.string,
    color: PropTypes.string,
    type: PropTypes.string,
    data: PropTypes.object,
};

export default Sparkline;
